// /integrations/hover — connect Hover once + see that it's healthy (Oct 1, 2026).

import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { hoverSync, startHoverConnect, HOVER_STATE_META, type HoverJob } from "../lib/hover";

interface Status {
  connected: boolean; connected_at: string | null; last_error: string | null; last_error_at: string | null;
  webhook_id: number | null; webhook_verified_at: string | null;
}

export default function HoverIntegration() {
  const [status, setStatus] = useState<Status | null>(null);
  const [jobs, setJobs] = useState<(HoverJob & { deals?: { homeowner_name: string | null; property_street: string | null } })[]>([]);
  const [banner, setBanner] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try { setStatus(await hoverSync<Status>({ action: "status" })); } catch (e) { setBanner({ kind: "err", text: (e as Error).message }); }
    const { data } = await supabase.from("hover_jobs")
      .select("*, deals(homeowner_name, property_street)").order("created_at", { ascending: false }).limit(25);
    setJobs((data ?? []) as any);
  }

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const r = p.get("hover_oauth");
    if (r === "success") setBanner({ kind: "ok", text: "Hover connected. New claims will now create Hover jobs automatically." });
    if (r === "error") {
      const d = p.get("detail") ?? "unknown";
      setBanner({ kind: "err", text: d === "invalid_state"
        ? "Security check failed (the link expired or was already used). Click Connect again."
        : `Hover connection failed: ${d}` });
    }
    void load();
  }, []);

  async function connect() {
    setBusy(true);
    try { await startHoverConnect(); } catch (e) { setBanner({ kind: "err", text: (e as Error).message }); setBusy(false); }
  }
  async function registerWebhook() {
    setBusy(true);
    try { await hoverSync({ action: "register_webhook" }); await load(); } catch (e) { setBanner({ kind: "err", text: (e as Error).message }); }
    setBusy(false);
  }

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900">🏠 Hover</h1>
        <p className="text-sm text-slate-600">
          When a claim is filed, the CRM creates a Hover job and puts it in the rep's Hover app. When Hover finishes,
          the measurements fill the PM Board and the PDF lands on the deal's Documents tab.
        </p>
      </div>

      {banner && (
        <div className={`rounded-lg px-3 py-2 text-sm ${banner.kind === "ok" ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800"}`}>{banner.text}</div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="font-semibold">Connection</div>
          {status && (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${status.connected ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
              {status.connected ? "Connected" : "Not connected"}
            </span>
          )}
        </div>
        {status?.connected_at && <div className="text-xs text-slate-500">Connected {new Date(status.connected_at).toLocaleString()}</div>}
        {status?.last_error && <div className="text-xs text-rose-700">Last error ({status.last_error_at && new Date(status.last_error_at).toLocaleString()}): {status.last_error}</div>}
        {status?.connected && (
          <div className="text-xs text-slate-600">
            Job updates from Hover: {status.webhook_verified_at ? "✓ webhook verified" : status.webhook_id ? "webhook registered, waiting for Hover to verify" : "not registered"}
            {!status.webhook_verified_at && (
              <button onClick={registerWebhook} disabled={busy} className="ml-2 underline disabled:opacity-50">Register again</button>
            )}
            <span className="text-slate-400"> · the CRM also checks open jobs every 20 minutes</span>
          </div>
        )}
        <button onClick={connect} disabled={busy}
          className="mt-1 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold disabled:opacity-50">
          {status?.connected ? "Reconnect Hover" : "Connect Hover"}
        </button>
        <p className="text-[11px] text-slate-500">You'll sign in to Hover as a Team Admin and approve "Bardin CRM", then come back here.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="font-semibold mb-2">Recent Hover jobs</div>
        {jobs.length === 0 ? <div className="text-xs text-slate-500">None yet.</div> : (
          <div className="divide-y divide-slate-100">
            {jobs.map((j) => (
              <a key={j.id} href={`/deal/${j.deal_id}`} className="flex items-center justify-between py-2 text-sm hover:bg-slate-50 px-1 rounded">
                <span className="truncate">{j.deals?.homeowner_name ?? "Deal"} <span className="text-slate-400">· {j.deals?.property_street}</span></span>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${HOVER_STATE_META[j.state].cls}`}>{HOVER_STATE_META[j.state].label}</span>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
